/**
 * Brand Context
 *
 * 品牌相關設定 (menu, color, name, etc....)
 */
import type { BrandInfo, BrandLoadDiagnostic } from 'api';
import { getBrandInfo, getBrandTabConfig } from 'api';
import type { Consumer, Context, FC, ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { reportBrandLoadFailure } from 'utils/brandLoadDiagnostics';

interface BrandProviderProps {
  children?: ReactNode;
  brandIdentify: string;
}
interface BrandContext {
  error?: any;
  brandInfo?: BrandInfo;
  initializing: boolean;
  retry?: () => Promise<void>;
  loading?: boolean;
}

type CreateBrandContext = () => {
  BrandConsumer: Consumer<BrandContext>;
  BrandProvider: FC<BrandProviderProps>;
  useBrand: () => BrandContext;
};

const RETRY_DELAYS = [500, 1000, 2000];

const isRetryableError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const e = error as { response?: { status?: number } };
  return !e.response || (e.response.status ?? 0) >= 500;
};

const createBrandProvider = (context: Context<BrandContext>) => {
  const BrandProvider: FC<BrandProviderProps> = ({
    children,
    brandIdentify,
  }) => {
    const latestBrandRequest = useRef(0);
    const [brandState, setBrandState] = useState<{
      error?: unknown;
      initializing: boolean;
      brandInfo?: BrandInfo;
      loading: boolean;
    }>({
      initializing: true,
      loading: false,
    });

    const loadBrand = async (isInitial: boolean) => {
      const requestId = ++latestBrandRequest.current;
      if (!isInitial) {
        setBrandState((prev) => ({ ...prev, loading: true }));
      }

      let lastError: unknown;
      let lastDiagnostic: BrandLoadDiagnostic = { kind: 'client_error' };
      let attempts = 0;

      const reportFailure = () =>
        reportBrandLoadFailure({
          brandIdentifier: brandIdentify,
          trigger: isInitial ? 'initial' : 'retry',
          attempts,
          diagnostic: lastDiagnostic,
        });

      for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
        if (attempt > 0) {
          await new Promise<void>((r) =>
            setTimeout(r, RETRY_DELAYS[attempt - 1]),
          );
        }

        attempts += 1;
        const {
          data: resp,
          error,
          diagnostic,
        } = await getBrandInfo(brandIdentify);
        lastDiagnostic = diagnostic || { kind: 'client_error' };

        if (resp) {
          // Merge the initial tab response into the same state update as the
          // brand response. This avoids a fast tabs response being discarded
          // before React has committed the initial brand state.
          const { data: tabConfig } = await getBrandTabConfig(brandIdentify);
          if (requestId !== latestBrandRequest.current) {
            return;
          }
          setBrandState((prev) => ({
            ...prev,
            brandInfo: tabConfig
              ? {
                  ...resp,
                  menu_color: tabConfig.menu_color || resp.menu_color,
                  tabs: tabConfig.tabs,
                  tab_config_version: tabConfig.version,
                }
              : resp,
            initializing: false,
            loading: false,
            error: undefined,
          }));
          return;
        }

        if (error && !isRetryableError(error)) {
          if (requestId !== latestBrandRequest.current) {
            return;
          }
          reportFailure();
          setBrandState((prev) => ({
            ...prev,
            error,
            brandInfo: undefined,
            initializing: false,
            loading: false,
          }));
          return;
        }

        lastError = error;
      }

      if (requestId !== latestBrandRequest.current) {
        return;
      }
      reportFailure();
      setBrandState((prev) => ({
        ...prev,
        error:
          lastError ??
          (lastDiagnostic.kind === 'invalid_response' ? true : undefined),
        brandInfo: undefined,
        initializing: false,
        loading: false,
      }));
    };

    useEffect(() => {
      if (!brandIdentify) {
        latestBrandRequest.current += 1;
        setBrandState((prev) => ({
          ...prev,
          error: true,
          initializing: false,
        }));
        return;
      }
      loadBrand(true);
      return () => {
        latestBrandRequest.current += 1;
      };
    }, [brandIdentify]); // eslint-disable-line react-hooks/exhaustive-deps

    const retry = async () => {
      await loadBrand(false);
    };

    return (
      <context.Provider
        value={{
          retry,
          ...brandState,
        }}
      >
        {children}
      </context.Provider>
    );
  };

  return BrandProvider;
};
export const createBrandContext: CreateBrandContext = () => {
  const context = createContext<BrandContext>({
    brandInfo: undefined,
    initializing: true,
  });
  context.displayName = 'BrandContext';

  return {
    BrandConsumer: context.Consumer,
    BrandProvider: createBrandProvider(context),
    useBrand: () => useContext(context),
  };
};
export const { BrandConsumer, BrandProvider, useBrand } = createBrandContext();
